import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface NavigationContextType {
  currentPath: string;
  previousPath: string;
  navigationHistory: string[];
  isMobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  goBack: () => void;
  canGoBack: boolean;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState('');
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setPreviousPath(navigationHistory[navigationHistory.length - 1] || '/');
    setNavigationHistory(prev => [...prev.slice(-4), location.pathname]); // Keep last 5 paths
    setMobileMenuOpen(false); // Close mobile menu on navigation
  }, [location.pathname]);

  const goBack = () => {
    if (navigationHistory.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  const canGoBack = navigationHistory.length > 1;

  return (
    <NavigationContext.Provider
      value={{
        currentPath: location.pathname,
        previousPath,
        navigationHistory,
        isMobileMenuOpen,
        setMobileMenuOpen,
        goBack,
        canGoBack,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};